import { useState, useMemo, useCallback } from 'react'
import { AdminLayout } from '~/components/AdminLayout'
import { 
  useDrivers, 
  useCreateDriver, 
  useUpdateDriver, 
  useDeleteDriver,
  type Driver, 
  type DriverStatus 
} from '~/api/drivers'
import { DriverModal, type DriverFormData } from '~/components/admin/DriverModal'
import styles from './drivers.module.css'

/**
 * Driver status filter options
 */
const DRIVER_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'ON_DELIVERY', label: 'On Delivery' },
  { value: 'OFFLINE', label: 'Offline' },
  { value: 'ON_BREAK', label: 'On Break' },
] as const

/**
 * Get the CSS class for a status badge
 */
function getStatusClass(status: DriverStatus): string {
  switch (status) {
    case 'AVAILABLE':
      return styles.statusAvailable
    case 'ON_DELIVERY':
      return styles.statusOnDelivery
    case 'OFFLINE':
      return styles.statusOffline
    case 'ON_BREAK':
      return styles.statusOnBreak
    default:
      return ''
  }
}

/**
 * Format status for display (replace underscores with spaces)
 */
function formatStatus(status: string): string {
  return status.replace(/_/g, ' ')
}

/**
 * Format rating as X.X with star
 */
function formatRating(rating: number): string {
  return rating.toFixed(1)
}

/**
 * Calculate average rating from drivers list
 */
function calculateAverageRating(drivers: Driver[]): number {
  if (drivers.length === 0) return 0
  const total = drivers.reduce((sum, driver) => sum + driver.rating, 0)
  return Math.round((total / drivers.length) * 10) / 10
}

/**
 * Admin Drivers Page - Data table showing all drivers with filtering and stats
 */
export function AdminDriversPage() {
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null)

  // Fetch all drivers
  const { data: drivers, isLoading, isError, error } = useDrivers()
  
  // Mutations
  const createDriver = useCreateDriver()
  const updateDriver = useUpdateDriver()
  const deleteDriver = useDeleteDriver()
  const isSaving = createDriver.isPending || updateDriver.isPending

  // Calculate stats from all drivers
  const stats = useMemo(() => {
    if (!drivers) return { total: 0, available: 0, onDelivery: 0, avgRating: 0 }
    return {
      total: drivers.length,
      available: drivers.filter((d) => d.status === 'AVAILABLE').length,
      onDelivery: drivers.filter((d) => d.status === 'ON_DELIVERY').length,
      avgRating: calculateAverageRating(drivers),
    }
  }, [drivers])

  // Filter drivers by status
  const statusFilteredDrivers = useMemo(() => {
    if (!drivers) return []
    if (!statusFilter) return drivers
    return drivers.filter((driver) => driver.status === statusFilter)
  }, [drivers, statusFilter])

  // Filter drivers by search query (client-side)
  const filteredDrivers = useMemo(() => {
    if (!searchQuery.trim()) return statusFilteredDrivers
    const query = searchQuery.toLowerCase()
    return statusFilteredDrivers.filter((driver) =>
      driver.licenseNumber.toLowerCase().includes(query) ||
      driver.vehiclePlate.toLowerCase().includes(query)
    )
  }, [statusFilteredDrivers, searchQuery])

  // Handle status filter change
  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value)
  }, [])

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  // Modal handlers
  const openCreateModal = useCallback(() => {
    setEditingDriver(null)
    setIsModalOpen(true)
  }, [])

  const openEditModal = useCallback((driver: Driver) => {
    setEditingDriver(driver)
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    setEditingDriver(null)
  }, [])

  const handleSave = useCallback(async (data: DriverFormData) => {
    if (editingDriver) {
      await updateDriver.mutateAsync({
        id: editingDriver.id,
        licenseNumber: data.licenseNumber,
        vehicleType: data.vehicleType,
        vehiclePlate: data.vehiclePlate,
        maxCapacityKg: data.maxCapacityKg,
        status: data.status,
      })
    } else {
      await createDriver.mutateAsync({
        userId: data.userId,
        licenseNumber: data.licenseNumber,
        vehicleType: data.vehicleType,
        vehiclePlate: data.vehiclePlate,
        maxCapacityKg: data.maxCapacityKg,
      })
    }
    closeModal()
  }, [editingDriver, createDriver, updateDriver, closeModal])

  const handleDelete = useCallback(async (id: string) => {
    await deleteDriver.mutateAsync(id)
  }, [deleteDriver])

  if (isLoading) {
    return (
      <AdminLayout title="DRIVERS">
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>Loading drivers...</span>
        </div>
      </AdminLayout>
    )
  }

  if (isError) {
    return (
      <AdminLayout title="DRIVERS">
        <div className={styles.errorContainer}>
          <span className={styles.errorText}>
            Error: {error?.message || 'Failed to load drivers'}
          </span>
        </div>
      </AdminLayout>
    )
  }

  const hasDrivers = filteredDrivers.length > 0
  const showEmptySearch = drivers && drivers.length > 0 && filteredDrivers.length === 0

  return (
    <AdminLayout title="DRIVERS">
      <div className={styles.driversPage}>
        {/* Stats Summary */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Total Drivers</span>
            <span className={styles.statValue}>{stats.total}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Available Now</span>
            <span className={`${styles.statValue} ${styles.statValueSuccess}`}>
              {stats.available}
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>On Delivery</span>
            <span className={`${styles.statValue} ${styles.statValueAccent}`}>
              {stats.onDelivery}
            </span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Average Rating</span>
            <span className={styles.statValue}>{stats.avgRating}</span>
          </div>
        </div>

        {/* Controls */}
        <div className={styles.controls}>
          <button
            className={styles.createButton}
            onClick={openCreateModal}
            aria-label="Create driver"
          >
            + ADD DRIVER
          </button>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by license or plate..."
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label="Search drivers"
          />
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={handleStatusChange}
            aria-label="Filter by status"
          >
            {DRIVER_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          {drivers && (
            <span className={styles.resultsCount}>
              {filteredDrivers.length} of {drivers.length} drivers
            </span>
          )}
        </div>

        {/* Table or Empty State */}
        {!hasDrivers ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyText}>
              {showEmptySearch ? 'No drivers found matching your search' : 'No drivers yet'}
            </span>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>LICENSE</th>
                  <th className={styles.th}>STATUS</th>
                  <th className={styles.th}>VEHICLE</th>
                  <th className={styles.th}>CAPACITY</th>
                  <th className={styles.th}>RATING</th>
                  <th className={styles.th}>DELIVERIES</th>
                  <th className={styles.th}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredDrivers.map((driver) => (
                  <tr key={driver.id} className={styles.tr}>
                    <td className={styles.td}>
                      <span className={styles.driverName}>{driver.licenseNumber}</span>
                    </td>
                    <td className={styles.td}>
                      <span className={`${styles.statusBadge} ${getStatusClass(driver.status)}`}>
                        {formatStatus(driver.status)}
                      </span>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.vehicleInfo}>
                        <span className={styles.vehicleType}>{driver.vehicleType}</span>
                        <span className={styles.vehiclePlate}>{driver.vehiclePlate}</span>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.capacity}>{driver.maxCapacityKg} kg</span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.rating}>
                        {formatRating(driver.rating)}
                        <span className={styles.ratingStar}>★</span>
                      </span>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.deliveries}>{driver.totalDeliveries}</span>
                    </td>
                    <td className={styles.td}>
                      <button
                        className={styles.editButton}
                        onClick={() => openEditModal(driver)}
                        aria-label={`Edit driver ${driver.licenseNumber}`}
                      >
                        ✎
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DriverModal
        driver={editingDriver}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={handleDelete}
        isSaving={isSaving}
      />
    </AdminLayout>
  )
}
